/**
 * @name QZTrayAuthModal
 * @author Keven Adan Bezerra | kevenadanb
 * @description Modal de autenticação para acesso às configurações do QZ Tray
 * @param {QZTrayAuthModalProps} props - Propriedades do modal
 * @returns {JSX.Element} Modal de autenticação
 * @package src/components/modals/QZTrayAuthModal
 */

import {
  Close as CloseIcon,
  Security as SecurityIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import React, { memo, useState } from "react";
import { useOperadorContext } from "../../contexts/OperadorContext";
import { useAlert } from "../../hooks/useAlert";

interface QZTrayAuthModalProps {
  /** Se o modal está aberto */
  open: boolean;
  /** Função para fechar o modal */
  onClose: () => void;
  /** Função chamada quando a autenticação é bem-sucedida */
  onAuthSuccess: () => void;
}

/**
 * Modal de autenticação para acesso às configurações do QZ Tray
 */
const QZTrayAuthModal: React.FC<QZTrayAuthModalProps> = memo(
  ({ open, onClose, onAuthSuccess }) => {
    const { operadorAtual } = useOperadorContext();
    const { showError } = useAlert();

    // Estados locais
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    /**
     * Limpa os campos ao fechar
     */
    const handleClose = (): void => {
      setEmail("");
      setPassword("");
      setShowPassword(false);
      setIsValidating(false);
      onClose();
    };

    /**
     * Valida credenciais
     */
    const handleValidate = async (): Promise<void> => {
      if (!email.trim() || !password.trim()) {
        showError("Preencha todos os campos");
        return;
      }

      setIsValidating(true);

      try {
        // Simula validação - pode ser substituída por uma validação real via API
        // Por enquanto, vamos validar se corresponde ao usuário logado
        if (operadorAtual?.email === email.trim()) {
          // Aqui você pode adicionar validação adicional se necessário
          onAuthSuccess();
          handleClose();
        } else {
          showError("Credenciais inválidas. Use suas credenciais de operador.");
        }
      } catch (error) {
        console.error("Erro na validação:", error);
        showError("Erro ao validar credenciais");
      } finally {
        setIsValidating(false);
      }
    };

    /**
     * Manipula envio do formulário
     */
    const handleSubmit = (e: React.FormEvent): void => {
      e.preventDefault();
      handleValidate();
    };

    /**
     * Toggle visibilidade da senha
     */
    const handleTogglePassword = (): void => {
      setShowPassword(!showPassword);
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
          },
        }}
      >
        <DialogTitle
          sx={{
            pb: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="h6"
            component="div"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            <SecurityIcon />
            Autenticação Requerida
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent dividers>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Para acessar as configurações de múltiplas impressoras, confirme
                suas credenciais de operador.
              </Typography>
            </Alert>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Email do Operador"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                autoFocus
                placeholder="Digite seu email"
                disabled={isValidating}
              />

              <TextField
                label="Senha"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                placeholder="Digite sua senha"
                disabled={isValidating}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePassword}
                        edge="end"
                        size="small"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              Use as mesmas credenciais utilizadas para fazer login no totem.
            </Typography>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={handleClose}
              color="inherit"
              disabled={isValidating}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isValidating || !email.trim() || !password.trim()}
            >
              {isValidating ? "Validando..." : "Confirmar"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    );
  },
);

QZTrayAuthModal.displayName = "QZTrayAuthModal";

export default QZTrayAuthModal;
